import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { reportsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  BarChart3, FileText, Download, Eye, DollarSign, Package, ShoppingCart,
  TrendingUp, Warehouse, Loader2, Calendar
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const statusColor = (s) => {
  const m = {
    paid: 'bg-green-100 text-green-800', unpaid: 'bg-yellow-100 text-yellow-800',
    overdue: 'bg-red-100 text-red-800', cancelled: 'bg-gray-100 text-gray-600',
    pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800',
    delivered: 'bg-green-100 text-green-800', draft: 'bg-gray-100 text-gray-600',
    received: 'bg-green-100 text-green-800',
  };
  return m[s] || 'bg-gray-100 text-gray-800';
};

const fmt = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

const Reports = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState('sales');
  const [loading, setLoading] = useState(false);

  // Sales
  const [sales, setSales] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // POs
  const [pos, setPos] = useState([]);
  const [poFilter, setPoFilter] = useState('all');
  const [selectedPO, setSelectedPO] = useState(null);

  // (POs and Inventory removed)

  const loadData = async (activeTab) => {
    setLoading(true);
    try {
      if (activeTab === 'sales') {
        const data = await reportsAPI.salesSummary(startDate, endDate);
        setSales(data);
      } else if (activeTab === 'invoices') {
        const data = await reportsAPI.invoices(invoiceFilter === 'all' ? '' : invoiceFilter);
        setInvoices(data);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load report data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(tab); }, [tab]);

  const handleExportCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).filter(k => k !== 'items');
    const rows = data.map(row => headers.map(h => {
      const v = row[h];
      return typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');
    }));
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${filename}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async (type, params = {}) => {
    try {
      await reportsAPI.exportExcel(type, params);
    } catch {
      toast({ title: 'Error', description: 'Failed to export Excel', variant: 'destructive' });
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Sales analytics and invoice reports</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-5">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="sales" data-testid="tab-sales"><BarChart3 className="h-4 w-4 mr-1.5" />Sales</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText className="h-4 w-4 mr-1.5" />Invoices</TabsTrigger>
          </TabsList>

          {/* ─── SALES SUMMARY ─── */}
          <TabsContent value="sales" className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">From</label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-44" data-testid="sales-start-date" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">To</label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-44" data-testid="sales-end-date" />
              </div>
              <Button onClick={() => loadData('sales')} variant="outline" data-testid="sales-filter-btn">
                <Calendar className="h-4 w-4 mr-1" /> Apply
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={() => handleExportExcel('sales-summary', { ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) })}
                  data-testid="sales-export-excel-btn">
                  <Download className="h-4 w-4 mr-1" /> Export Excel
                </Button>
              </div>
            </div>

            {loading ? <LoadingSkeleton /> : sales && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <SummaryCard icon={DollarSign} label="Total Revenue" value={`$${fmt(sales.total_revenue)}`} color="sky" testId="sales-total-revenue" />
                  <SummaryCard icon={DollarSign} label="Paid Revenue" value={`$${fmt(sales.paid_revenue)}`} color="green" testId="sales-paid-revenue" />
                  <SummaryCard icon={ShoppingCart} label="Total Orders" value={sales.total_orders} color="indigo" testId="sales-total-orders" />
                  <SummaryCard icon={TrendingUp} label="Avg Order Value" value={`$${fmt(sales.avg_order_value)}`} color="amber" testId="sales-avg-order" />
                </div>

                {/* Top Products & Customers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="border shadow-sm">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-600">Top Products by Revenue</CardTitle></CardHeader>
                    <CardContent>
                      {sales.top_products.length === 0 ? <p className="text-sm text-slate-400">No data</p> : (
                        <div className="space-y-2">
                          {sales.top_products.map((p, i) => (
                            <div key={i} className="flex items-center justify-between text-sm" data-testid={`top-product-${i}`}>
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                                <span className="text-slate-700 font-medium">{p.product}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-slate-800">${fmt(p.revenue)}</span>
                                <span className="text-xs text-slate-400 ml-2">({p.quantity} units)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-600">Top Customers by Revenue</CardTitle></CardHeader>
                    <CardContent>
                      {sales.top_customers.length === 0 ? <p className="text-sm text-slate-400">No data</p> : (
                        <div className="space-y-2">
                          {sales.top_customers.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-sm" data-testid={`top-customer-${i}`}>
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                                <span className="text-slate-700 font-medium">{c.name}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-bold text-slate-800">${fmt(c.revenue)}</span>
                                <span className="text-xs text-slate-400 ml-2">({c.orders} orders)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Monthly Trend */}
                {sales.monthly_trend.length > 0 && (
                  <Card className="border shadow-sm">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-600">Monthly Sales Trend</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex items-end gap-1 h-32" data-testid="monthly-chart">
                        {sales.monthly_trend.map((m, i) => {
                          const max = Math.max(...sales.monthly_trend.map(t => t.revenue));
                          const h = max > 0 ? (m.revenue / max) * 100 : 0;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-[9px] text-slate-500 font-medium">${(m.revenue / 1000).toFixed(1)}k</span>
                              <div className="w-full bg-gradient-to-t from-sky-500 to-teal-400 rounded-t" style={{ height: `${Math.max(h, 4)}%` }} />
                              <span className="text-[9px] text-slate-400">{m.month.slice(5)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* ─── INVOICES ─── */}
          <TabsContent value="invoices" className="space-y-5">
            <div className="flex items-center justify-between">
              <Select value={invoiceFilter} onValueChange={v => { setInvoiceFilter(v); }}>
                <SelectTrigger className="w-44" data-testid="invoice-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Invoices</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => loadData('invoices')} data-testid="invoice-refresh-btn">Refresh</Button>
                <Button variant="outline" onClick={() => handleExportCSV(invoices, 'invoices')} data-testid="invoice-export-btn">
                  <Download className="h-4 w-4 mr-1" /> Export CSV
                </Button>
                <Button variant="outline" onClick={() => handleExportExcel('invoices', invoiceFilter !== 'all' ? { status: invoiceFilter } : {})}
                  data-testid="invoice-export-excel-btn">
                  <Download className="h-4 w-4 mr-1" /> Export Excel
                </Button>
              </div>
            </div>

            {loading ? <LoadingSkeleton /> : (
              <Card className="border shadow-sm">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="invoices-table">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Invoice #</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Customer</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Date</th>
                          <th className="text-right py-3 px-4 text-xs font-bold text-slate-500 uppercase">Amount</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-slate-500 uppercase">Status</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-slate-500 uppercase">Payment</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-slate-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {invoices.length === 0 ? (
                          <tr><td colSpan={7} className="py-8 text-center text-slate-400">No invoices found</td></tr>
                        ) : invoices.map((inv) => (
                          <tr key={inv.order_id} className="hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 font-mono text-sm font-medium text-sky-700">{inv.invoice_number || '-'}</td>
                            <td className="py-3 px-4">
                              <p className="text-sm font-medium text-slate-800">{inv.customer_name}</p>
                              {inv.customer_company && <p className="text-xs text-slate-400">{inv.customer_company}</p>}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">{fmtDate(inv.created_at)}</td>
                            <td className="py-3 px-4 text-right text-sm font-bold text-slate-800">${fmt(inv.total)}</td>
                            <td className="py-3 px-4 text-center"><Badge className={statusColor(inv.invoice_status)}>{inv.invoice_status}</Badge></td>
                            <td className="py-3 px-4 text-center"><Badge className={statusColor(inv.payment_status)}>{inv.payment_status}</Badge></td>
                            <td className="py-3 px-4 text-center">
                              <Button size="sm" variant="ghost" onClick={() => setSelectedInvoice(inv)} data-testid={`view-invoice-${inv.order_id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>
      </div>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedInvoice && (
            <>
              <DialogHeader><DialogTitle className="text-xl font-bold">Invoice {selectedInvoice.invoice_number}</DialogTitle></DialogHeader>
              <div className="space-y-5" data-testid="invoice-detail-dialog">
                <div className="flex justify-between border-b pb-4">
                  <div>
                    <h3 className="text-lg font-bold text-sky-700">AT1S AirTech Solutions</h3>
                    <p className="text-xs text-slate-500">11615 Brittmoore Park Dr, Houston, TX 77041</p>
                    <p className="text-xs text-slate-500">(832) 228 7463</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Date</p>
                    <p className="font-medium text-sm">{fmtDate(selectedInvoice.created_at)}</p>
                    <p className="text-xs text-slate-400 mt-1">Order</p>
                    <p className="font-mono text-sm">{selectedInvoice.order_id}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 mb-1">BILL TO</p>
                  <p className="font-semibold text-slate-800">{selectedInvoice.customer_name}</p>
                  {selectedInvoice.customer_company && <p className="text-sm text-slate-500">{selectedInvoice.customer_company}</p>}
                  <p className="text-sm text-slate-500">{selectedInvoice.shipping_address}</p>
                </div>
                <table className="w-full border rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-bold text-slate-500">Description</th>
                      <th className="text-right py-2 px-3 text-xs font-bold text-slate-500">Qty</th>
                      <th className="text-right py-2 px-3 text-xs font-bold text-slate-500">Price</th>
                      <th className="text-right py-2 px-3 text-xs font-bold text-slate-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedInvoice.items || []).map((it, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="py-2 px-3 text-sm">Air Filter {it.size} - MERV {it.merv}</td>
                        <td className="py-2 px-3 text-right text-sm">{it.quantity}</td>
                        <td className="py-2 px-3 text-right text-sm">${fmt(it.price)}</td>
                        <td className="py-2 px-3 text-right text-sm font-medium">${fmt(it.quantity * it.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="border-t pt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>${fmt(selectedInvoice.subtotal)}</span></div>
                  {selectedInvoice.discount > 0 && <div className="flex justify-between"><span className="text-green-600">Discount</span><span className="text-green-600">-${fmt(selectedInvoice.discount)}</span></div>}
                  <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>{selectedInvoice.shipping === 0 ? 'FREE' : `$${fmt(selectedInvoice.shipping)}`}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tax</span><span>${fmt(selectedInvoice.tax)}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total</span><span className="text-sky-700">${fmt(selectedInvoice.total)}</span></div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 flex justify-between text-sm">
                  <span className="text-slate-500">Payment: {selectedInvoice.payment_method || '-'}</span>
                  <Badge className={statusColor(selectedInvoice.payment_status)}>{selectedInvoice.payment_status}</Badge>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

// ─── Helper Components ───

const SummaryCard = ({ icon: Icon, label, value, color, testId }) => {
  const colors = {
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-semibold opacity-70">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
    </div>
    <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
  </div>
);

export default Reports;
